import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useChatWs } from '../chat/useChatWs';
import { publishChatWsEvent, setChatWsSend } from '../chat/chatWsBus';
import { usePresence } from '../chat/usePresence';
import * as chatApi from '../../api/chat';
import { tasksApi } from '../../hrms/api/tasks';
import {
  bulkHydrate as bulkHydrateChat,
  incrementForConversation,
  clearConversation,
  enrichFromConversations,
} from '../../redux/slices/chatUnread';
import {
  bulkHydrate as bulkHydrateTasks,
  pushNotification,
  removeNotificationsForTask,
} from '../../redux/slices/notifications';
import { selectMuted } from '../../redux/slices/convPrefs';
import { useAuth } from '../../contexts/AuthContext';
import { useCall } from '../../contexts/CallContext';
import { useGroupCall } from '../../contexts/GroupCallContext';
import { useDndQuietMode } from '../../contexts/DndQuietModeContext';
import { buildChatUnreadEntries, buildConvMetaMap } from './buildChatUnreadEntries';
import {
  formatCallEventPreview,
  isCallEventMessage,
  isOwnCallEvent,
} from '../../utils/chat/callEventPreview';
import { isLeaveSystemMessage } from '../../utils/chat/leaveSystemMessage';
import { getActiveConversationId } from '../../utils/chat/activeConversation';
import {
  claimDesktopMessageNotification,
  shouldShowDesktopMessageNotification,
} from '../../utils/chat/desktopNotification';
import { DRAFT_CHANGED_EVENT } from './draftRefresh';
import { showDesktopNotification, onDesktopNotificationClick } from '../../utils/electron';
import { playNotificationSound, unlockAudioOnGesture } from '../../utils/taskNotificationSound';
import { emitAttendanceClockUpdated } from '../../utils/attendanceClockEvents';
import { isCallStateEnvelope } from '../../types/calls';
import { callEventEnvelopeV2ToLegacy, isCallEventEnvelopeV2 } from '../../types/calls';
import { getCallSnapshotV2 } from '../../api/calls';
import { createCallReconciler } from '../../contexts/callReconciliation';
import { isWindowVisible } from '../../utils/windowVisibility';
import {
  SESSION_PLATFORM,
  forceLogout,
  forceLogoutMessage,
  getSessionDeviceId,
  shouldForceLogout,
} from '../../utils/session';

export function useGlobalNotificationsWs() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  // Voice-call signaling rides this always-on socket: inbound call-* frames are
  // routed to the call layer, and its outbound send fn is registered below.
  const callApi = useCall();
  const groupCallApi = useGroupCall();
  const { isDndQuiet, refreshDndQuietMode } = useDndQuietMode();
  const { handlePresenceEvent } = usePresence();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUserId = user?.id ?? '';
  // Mirror the muted list into a ref so the WS callback below can read the
  // latest value without re-binding every time the user toggles mute.
  const mutedIds = useSelector(selectMuted);
  const mutedIdsRef = useRef<string[]>(mutedIds);
  useEffect(() => { mutedIdsRef.current = mutedIds; }, [mutedIds]);
  // Mirror the chat-unread avatar map so an incoming call-invite that lacks a
  // caller photo (older backend) can borrow it from the conversation list we
  // already have, instead of falling back to an initial on the call screen.
  const unreadById = useSelector((s: any) => s.chatUnread?.byId ?? {});
  const unreadByIdRef = useRef<Record<string, { avatarUrl: string | null }>>(unreadById);
  useEffect(() => { unreadByIdRef.current = unreadById; }, [unreadById]);
  const callReconcilerRef = useRef(createCallReconciler(getCallSnapshotV2));

  // Browsers block Web Audio until first user gesture — arm the unlock once.
  useEffect(() => {
    unlockAudioOnGesture();
  }, []);

  // Navigate to the right conversation when a desktop notification is clicked.
  useEffect(() => {
    const unsub = onDesktopNotificationClick(({ conversationId }) => {
      if (conversationId) {
        navigate(`/chat?open=${encodeURIComponent(conversationId)}`);
      } else {
        navigate('/chat');
      }
    });
    return unsub;
  }, [navigate]);

  // Track the currently-open conversation so we don't increment unread for it.
  // Chat.tsx reads `?open=<id>` from the URL on mount, so the same param tells
  // us which conversation is being viewed.
  const activeConvIdRef = useRef<string | null>(null);
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
    if (location.pathname !== '/chat') {
      activeConvIdRef.current = null;
      return;
    }
    const params = new URLSearchParams(location.search);
    activeConvIdRef.current = params.get('open');
  }, [location.pathname, location.search]);

  // Initial hydration. Runs once per mount (sidebar is mounted for the whole
  // app lifetime, so once per app load is correct).
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;

    (async () => {
      try {
        const convs = await chatApi.listConversations();
        if (cancelled) return;
        dispatch(bulkHydrateChat(buildChatUnreadEntries(convs, currentUserId)));
        // Backfill avatars/names on unread + read-history rows from the full
        // conversation list, so notification rows show real profile pictures
        // (incl. DM partner photos) instead of coloured monograms.
        dispatch(enrichFromConversations(buildConvMetaMap(convs, currentUserId)));
      } catch {
        // Leave slice empty; bell shows 0 until a frame arrives.
      }
      try {
        const resp = await tasksApi.getNotifications(true, 50);
        if (cancelled) return;
        const items = Array.isArray(resp?.data) ? resp.data : resp?.data?.items ?? [];
        dispatch(bulkHydrateTasks(items));
      } catch {
        // Leave slice empty.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUserId, dispatch]);

  // The second WS connection. Token is read inside useChatWs from storage.
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;

  // The always-on socket is created below with this handler; the ref lets the
  // handler ack delivery through the same socket it received the frame on.
  const wsSendRef = useRef<((data: object) => boolean) | null>(null);

  const wsSend = useChatWs(token, (evt: any) => {
    if (evt?.type === 'presence' || evt?.type === 'presence_snapshot') {
      handlePresenceEvent(evt);
    }

    // The user's own DND/Away window starting or ending arrives as a system
    // message in their conversations. Refreshing quiet mode from it is what
    // lets the context drop its 30-second poll.
    if (evt?.type === 'new_message' && evt?.message?.message_type === 'system') {
      const body = evt.message.body;
      if (
        body?.user_id === currentUserId &&
        (body.kind === 'away_start' || body.kind === 'away_end')
      ) {
        void refreshDndQuietMode?.();
      }
    }

    switch (evt?.type) {
      case 'force_logout': {
        // Another device took this platform's single-session slot. Filtered
        // by platform + device id so taking the web slot never kills the
        // desktop app, and so our own fresh login doesn't kill us.
        void (async () => {
          const deviceId = await getSessionDeviceId();
          if (shouldForceLogout(evt, { platform: SESSION_PLATFORM, deviceId })) {
            // Unlike the heartbeat's 409 body, this frame carries a bare
            // device name — forceLogout() expects a complete sentence, and a
            // deactivation frame has no device to name at all, so
            // forceLogoutMessage builds the right one for each case.
            forceLogout(forceLogoutMessage(evt));
          }
        })();
        break;
      }
      case 'ws_connected': {
        void callApi.recoverCalls();
        void groupCallApi.recoverCalls();
        break;
      }
      case 'attendance_updated': {
        if (evt.user_id !== currentUserId) break;
        if (typeof evt.clocked_in !== 'boolean') break;
        emitAttendanceClockUpdated({
          action: evt.clocked_in ? 'in' : 'out',
          clockedIn: evt.clocked_in,
          source: typeof evt.source === 'string' ? evt.source : undefined,
          userId: evt.user_id,
          sourceId: 'attendance-realtime-socket',
          occurredAt: typeof evt.occurred_at === 'string' ? evt.occurred_at : undefined,
        });
        break;
      }
      case 'meetly_status_changed': {
        // Re-broadcast as a DOM event so the Meetly workspace can listen without
        // subscribing to the whole notification socket. Same contract the website
        // uses, so the ported pages work unchanged.
        if (typeof evt.meeting_id !== 'string' || typeof evt.status !== 'string') break;
        window.dispatchEvent(new CustomEvent('meetly-status-changed', {
          detail: {
            meetingId: evt.meeting_id,
            status: evt.status,
            terminal: evt.status === 'COMPLETED' || evt.status === 'FAILED',
            progress: evt.progress && typeof evt.progress === 'object' ? evt.progress : null,
          },
        }));
        break;
      }
      // A notification the server has retired because it is no longer true
      // (OVERDUE on a task whose due date moved out, or that was completed).
      case 'task_notifications_cleared': {
        if (evt.task_id && evt.kind) {
          dispatch(removeNotificationsForTask({ taskId: evt.task_id, kind: evt.kind }));
        }
        break;
      }
      case 'task_notification': {
        dispatch(
          pushNotification({
            id: evt.notification_id,
            task_id: evt.task_id,
            user_id: '',
            kind: evt.kind,
            title: evt.title,
            body: evt.body || '',
            is_read: false,
            read_at: null,
            email_sent: false,
            email_sent_at: null,
            created_at: evt.created_at,
          })
        );
        if (!isDndQuiet) {
          showDesktopNotification({
            title: evt.title || 'Task Update',
            body: evt.body || '',
          });
          // Two-tone chime; respects the user's sound preference. Backend
          // already skips publishing muted-task frames, so no extra filter here.
          playNotificationSound();
        }
        break;
      }
      case 'new_message': {
        const msg = evt.message;
        if (!msg) break;
        // Suppress increments for the user's own messages.
        if (msg.sender_id === currentUserId) break;
        // Shared call_event messages have sender_id=null, so also suppress
        // the copy broadcast back to the caller.
        if (isOwnCallEvent(msg, currentUserId)) break;
        // Leave announcements fan out to every conversation the on-leave user
        // belongs to; they show in-thread as a pill and must not raise unread
        // counts or overwrite the conversation preview across the whole list.
        if (isLeaveSystemMessage(msg)) break;

        // Delivery ack: this frame reached the client, so the sender's single
        // tick can become a gray double tick. The server records delivery
        // only from a client ack (never from presence), and this hook is the
        // one that is mounted on every page — the Chat page's own ack covers
        // it only while that page is open. Repeats are no-ops server-side.
        if (msg.message_type !== 'system' && !isCallEventMessage(msg) && msg.id) {
          try {
            wsSendRef.current?.({ type: 'delivered', conversation_id: msg.conversation_id, message_ids: [msg.id] });
          } catch { /* noop */ }
        }

        // The sidebar socket must not create an unread row for a conversation
        // being read, but it still owns the native-notification fallback when
        // that active Electron window is hidden. The chat-page socket can be
        // reconnecting at exactly that moment.
        const isActiveConversation =
          msg.conversation_id === activeConvIdRef.current ||
          msg.conversation_id === getActiveConversationId();
        const isCallEvent = isCallEventMessage(msg);
        const notificationName = msg.conversation_name ?? msg.sender_name ?? (isCallEvent ? 'Call' : undefined);
        const notificationContent = formatCallEventPreview(msg, currentUserId) ?? msg.content ?? null;
        if (!isActiveConversation) {
          dispatch(
            incrementForConversation({
              conversationId: msg.conversation_id,
              lastActivity: msg.created_at ?? new Date().toISOString(),
            // Backend `new_message` envelope does not yet include
            // `conversation_name`. For DMs `sender_name` equals the peer's
            // name (which IS the conversation's display name); for groups
            // it shows the sender — acceptable until the user refreshes
            // and `bulkHydrateChat` picks up the proper group name.
            name: notificationName,
            // The `new_message` envelope carries `sender_avatar` (never a
            // conversation-level avatar). For DMs the sender IS the peer, so
            // this is the correct notification avatar; for groups it shows the
            // sender's photo until a refresh re-hydrates the proper group one.
            avatarUrl: msg.sender_avatar ?? null,
            content: notificationContent,
              lastMessageId: msg.id,
            })
          );
        }
        if (!isActiveConversation && isCallEvent && msg.conversation_id && !msg.conversation_name && !msg.sender_name) {
          chatApi.getConversation(msg.conversation_id)
            .then((conv) => {
              dispatch(enrichFromConversations(buildConvMetaMap([conv], currentUserId)));
            })
            .catch(() => { /* next listConversations hydration will backfill */ });
        }
        // Either socket may receive the event first. The claim keeps the OS
        // popup single-shot while letting this durable socket cover a stale or
        // reconnecting chat-page socket.
        const isMuted = mutedIdsRef.current.includes(msg.conversation_id);
        const isMessageNotification = shouldShowDesktopMessageNotification({
          isDndQuiet,
          isMine: false,
          isMuted,
          messageType: msg.message_type,
          isActiveConversation,
          isWindowVisible: isWindowVisible(),
        });
        const isCallNotification =
          isCallEvent &&
          !isDndQuiet &&
          !isMuted &&
          !isActiveConversation &&
          pathnameRef.current !== '/chat';
        if ((isMessageNotification || isCallNotification) && claimDesktopMessageNotification(msg.id)) {
          const body = notificationContent
            ? (notificationContent.length > 120 ? notificationContent.slice(0, 117) + '…' : notificationContent)
            : 'New message';
          showDesktopNotification({
            title: notificationName || 'TeamLio',
            body,
            conversationId: msg.conversation_id,
            iconUrl: msg.sender_avatar ?? null,
          });
        }
        break;
      }
      case 'away_back_reminder': {
        const title = evt.user_name
          ? `${evt.user_name} is back`
          : 'Back from away';
        const body = evt.message || 'They are back from away.';
        const conversationId = evt.conversation_id;
        if (conversationId) {
          dispatch(
            incrementForConversation({
              conversationId,
              lastActivity: evt.ended_at ?? evt.created_at ?? new Date().toISOString(),
              name: evt.user_name ?? 'Back from away',
              avatarUrl: evt.user_avatar ?? null,
              content: body,
            })
          );
        }
        if (isDndQuiet) break;
        toast(body, {
          action: conversationId
            ? {
                label: 'Open',
                onClick: () => navigate(`/chat?open=${encodeURIComponent(conversationId)}`),
              }
            : undefined,
        });
        showDesktopNotification({
          title,
          body,
          conversationId,
        });
        playNotificationSound();
        break;
      }
      // `conversation_read` is how another of my devices (the phone) reports
      // reading a conversation; receipts only go to the other members.
      case 'conversation_read':
      case 'seen_receipt': {
        // Server tells us our own user has read this conversation.
        if (evt.user_id !== currentUserId) break;
        if (!evt.conversation_id) break;
        dispatch(clearConversation(evt.conversation_id));
        break;
      }
      case 'draft_changed': {
        // A cross-org draft a reviewer can see was created/updated/removed.
        // Ping the draft count/list consumers so they refetch — this is what
        // replaced the old 60s polling of the drafts endpoints.
        window.dispatchEvent(new Event(DRAFT_CHANGED_EVENT));
        break;
      }
      case 'call-state': {
        // Only authoritative, structurally valid lifecycle envelopes may reach
        // call providers. Direct signaling remains a separate P2P media path.
        if (isCallEventEnvelopeV2(evt)) {
          const result = callReconcilerRef.current.accept(evt);
          if (result === 'needs-recovery') {
            // The reconciler starts a bounded recovery fetch. Apply that
            // authenticated snapshot directly when it resolves; the gapped
            // frame itself is never allowed to mutate UI state.
            void getCallSnapshotV2(evt.callId).then(snapshot => {
              const recovered = {
                ...evt,
                eventType: 'session-updated' as const,
                eventId: snapshot.eventId,
                sequenceNumber: snapshot.version,
                serverTimestamp: snapshot.serverTimestamp,
                participant: null,
                snapshot,
              };
              const legacy = callEventEnvelopeV2ToLegacy(recovered);
              if (legacy.snapshot.mediaTopology === 'p2p' && !legacy.snapshot.isGroup) {
                callApi.handleCallState(legacy);
              } else {
                groupCallApi.handleCallState(legacy);
              }
            }).catch(() => { /* reconnect recovery retries later */ });
            break;
          }
          if (result !== 'accepted') break;
          const legacy = callEventEnvelopeV2ToLegacy(evt);
          if (legacy.snapshot.mediaTopology === 'p2p' && !legacy.snapshot.isGroup) {
            callApi.handleCallState(legacy);
          } else {
            groupCallApi.handleCallState(legacy);
          }
          break;
        }
        if (!isCallStateEnvelope(evt)) break;
        if (evt.snapshot.mediaTopology === 'p2p' && !evt.snapshot.isGroup) {
          callApi.handleCallState(evt);
        } else {
          groupCallApi.handleCallState(evt);
        }
        break;
      }
      case 'call-invite':
      case 'call-answer':
      case 'ice-candidate':
      case 'call-reject':
      case 'call-cancel':
      case 'call-end':
      case 'call-busy': {
        // 1-to-1 voice-call signaling — handled only on this always-on socket
        // (Chat.tsx's WS ignores call frames) so there is no double-handling.
        // Fallback: borrow the caller's photo from the chat list if the invite
        // arrived without one (pre-effective_avatar_url backend).
        if (evt.type === 'call-invite' && !evt.from_avatar_url && evt.conversation_id) {
          const borrowed = unreadByIdRef.current?.[evt.conversation_id]?.avatarUrl;
          if (borrowed) evt.from_avatar_url = borrowed;
        }
        callApi.handleSignal(evt);
        break;
      }
      case 'scheduled_call_due': {
        if (isDndQuiet) break;
        const sc = evt.scheduled_call;
        if (!sc) break;
        if (sc.role === 'owner' && sc.target_user_id) {
          toast(`Time to call ${sc.target_name || 'your contact'}`, {
            action: {
              label: 'Call',
              onClick: () => callApi.startCall(sc.conversation_id, sc.target_user_id, sc.target_name, sc.target_avatar_url),
            },
          });
        } else {
          toast(`${sc.target_name || 'Someone'} is ready to call you`);
        }
        break;
      }
      default:
        break;
    }

    // Single-socket architecture: every frame is republished so the pages
    // that used to open their own socket (Chat, Calls) receive it from here.
    publishChatWsEvent(evt);
  });

  wsSendRef.current = wsSend;

  // Give the call layer this always-on socket's send fn so it can ship SDP/ICE.
  useEffect(() => {
    callApi.bindSend(wsSend);
  }, [wsSend, callApi]);

  // ...and the shared bus, so subscribed pages write on this same socket.
  useEffect(() => {
    setChatWsSend(wsSend);
    return () => setChatWsSend(null);
  }, [wsSend]);

}
